/**
 * A day's unit — `docs/visit-page.md` §10.1, Board 5 B: the popover from a
 * day cell (330), *WET SAW · FRI OCT 23* — **Decide in the morning** (*the
 * yard picks one at 6:30 — 2 free tonight*) and the units of the type that
 * day, a unit in the shop disabled — and the line *A unit picked here is held
 * for the day; one already on another visit that day isn't offered.*
 *
 * Picking writes `visit_equipment_assignments` for that day through
 * `PUT /visits/:id/days/:n/equipment/:typeId` (`EquipmentPort.holdUnit`); the
 * exclusion constraint guards double-booking, and a refusal is said here.
 *
 * On a phone it is the visit page's sheet from the bottom (§19.2), the same
 * body.
 */
import { useState } from 'react'
import type { DayRef, EquipmentRead, HoldUnitInput, UnitOption, UnitPicker as UnitPickerData, VisitRead } from '@rowted/shared'
import { cn } from '../lib/cn.js'
import { Popover } from '../components/overlays.js'
import { useVisitTab, useVisitWrite } from './api.js'
import { UNIT_PICKER } from './copy.js'
import { RadioDot } from './fields.js'
import { dayText, localTime } from './format.js'
import { VisitPopup } from './sheet.js'

export interface PickingCell {
  typeId: string
  typeName: string
  day: DayRef
  anchor: HTMLElement | null
}

/** What a unit's row says under its tag. */
function whereWords(option: UnitOption, day: DayRef): string | null {
  switch (option.where.kind) {
    case 'this_visit_today': return UNIT_PICKER.thisVisitToday
    case 'at_yard': return UNIT_PICKER.atYardFree(dayText(day.date, 'weekday'))
    case 'in_shop': return option.where.why ? UNIT_PICKER.inShop(option.where.why) : null
  }
}

function Row({ on, disabled, title, sub, phone, onPick }: {
  on: boolean
  disabled: boolean
  title: string
  sub: string | null
  phone: boolean
  onPick: () => void
}) {
  return (
    <button
      type="button"
      role="menuitemradio"
      aria-checked={on}
      disabled={disabled}
      onClick={onPick}
      className={cn('mx-1 flex w-[calc(100%-8px)] items-center gap-2.5 rounded-control px-3 text-left',
        phone ? 'min-h-[52px] py-2' : 'py-[9px]',
        on && 'bg-green-band-bg',
        disabled ? 'cursor-not-allowed opacity-50' : !on && 'hover:bg-hover')}
    >
      <RadioDot on={on} size={16} />
      <span className="flex min-w-0 flex-col">
        <b className="text-[12.5px]">{title}</b>
        {sub && <span className="text-[11.5px] text-text-muted">{sub}</span>}
      </span>
    </button>
  )
}

function PickerBody({ data, phone, busy, error, onPick }: {
  data: UnitPickerData
  phone: boolean
  busy: boolean
  error: string | null
  onPick: (choice: HoldUnitInput) => void
}) {
  const selected = data.selected
  return (
    <div role="menu" aria-label={UNIT_PICKER.title(data.type.name, dayText(data.day.date, 'date'))}
      className={cn(phone ? '-mx-4 -my-3.5 py-1.5' : 'pb-1.5 pt-1')}>
      {!phone && (
        <div className="px-3.5 pb-1.5 pt-[9px] text-[10.5px] font-semibold uppercase tracking-[.06em] text-text-faint">
          {UNIT_PICKER.title(data.type.name, dayText(data.day.date, 'date'))}
        </div>
      )}
      <Row
        on={selected?.kind === 'decide'}
        disabled={busy}
        phone={phone}
        title={UNIT_PICKER.decide}
        sub={UNIT_PICKER.decideSub(localTime(data.decide.pickAt), String(data.decide.freeTonight))}
        onPick={() => onPick({ decideInTheMorning: true })}
      />
      {data.units.map((option) => (
        <Row
          key={option.unit.id}
          on={selected?.kind === 'unit' && selected.unitId === option.unit.id}
          disabled={busy || option.disabled}
          phone={phone}
          title={option.unit.tag}
          sub={whereWords(option, data.day)}
          onPick={() => onPick({ equipmentId: option.unit.id })}
        />
      ))}
      {error && <p className="m-0 px-3.5 pt-2 text-[12px] text-danger">{error}</p>}
      <p className="m-0 mt-1 border-t border-line-faint px-3.5 pb-0.5 pt-2 text-[11.5px] leading-[1.4] text-text-muted">
        {UNIT_PICKER.footer}
      </p>
    </div>
  )
}

export function UnitPicker({ visitId, cell, phone, onClose, onWritten }: {
  visitId: string
  read: VisitRead
  cell: PickingCell
  phone: boolean
  onClose: () => void
  onWritten: (next: EquipmentRead) => void
}) {
  const { data } = useVisitTab<UnitPickerData>(visitId, `/days/${cell.day.index}/equipment/${cell.typeId}`)
  const write = useVisitWrite(visitId)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function pick(choice: HoldUnitInput) {
    setBusy(true)
    setError(null)
    try {
      const result = await write('PUT', `/days/${cell.day.index}/equipment/${cell.typeId}`, choice, { tab: 'equipment' })
      if (result.tab) onWritten(result.tab as EquipmentRead)
      onClose()
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause))
    } finally {
      setBusy(false)
    }
  }

  if (phone) {
    if (!data) return null
    return (
      <VisitPopup width={332} phone title={UNIT_PICKER.title(data.type.name, dayText(data.day.date, 'date'))} onClose={onClose}>
        <PickerBody data={data} phone busy={busy} error={error} onPick={(choice) => void pick(choice)} />
      </VisitPopup>
    )
  }

  return (
    <Popover
      open={Boolean(data)}
      onClose={onClose}
      anchor={cell.anchor}
      width={332}
      snap
      label={UNIT_PICKER.title(cell.typeName, dayText(cell.day.date, 'date'))}
      className="leading-[normal] tabular-nums"
    >
      {/* The board's crop is the popover's own box, border and all — so the mark goes on the box. */}
      <div ref={(node) => { node?.parentElement?.setAttribute('data-as-drawn', 'vp-05 B') }}>
        {data && <PickerBody data={data} phone={false} busy={busy} error={error} onPick={(choice) => void pick(choice)} />}
      </div>
    </Popover>
  )
}
